import './loanDocumentUploadAllPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import LinearProgress from '@mui/material/LinearProgress';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { splitFilename } from '@thesavingsgroup/utils/splitFilename';
import { forEach, initial, reduce, uniqueId } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { ErrorCode, FileError, FileRejection } from 'react-dropzone';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { config } from '../../../config';
import { LoanDocumentEnum } from '../../../store/loanDocumentStore/enums/LoanDocumentEnum';
import { useLoanDocumentStoreForType } from '../../../store/loanDocumentStore/useLoanDocumentStoreForType';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';
import Dropzone from '../Dropzone';
import { LoanDocumentList } from '../shared/LoanDocumentList';
import { LoanDocumentUploadedList } from '../shared/LoanDocumentUploadedList';

export function Component(): JSX.Element {
  const {
    addDocuments,
    confirm,
    hasProvidedDocuments,
    isDuplicateFile,
    loadDocuments,
    percent,
    providedDocuments,
    reset,
    uploadedDocuments,
  } = useLoanDocumentStoreForType(LoanDocumentEnum.RIC);

  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const { handleSubmit } = useForm();

  const [rejections, setRejections] = useState<[string, string][]>([]);
  const [removalStarted, setRemovalStarted] = useState<boolean>(false);

  const MAXIMUM_FILE_NAME_LENGTH = 12;
  const TRUNCATED_FILE_NAME_PREFIX_LENGTH = 4;
  const TRUNCATED_FILE_NAME_SUFFIX_LENGTH = 4;
  const AUTO_HIDE_DURATION = 5000;
  const ALERT_REMOVAL_DURATION = 500;

  useEffect(() => {
    loadDocuments();

    // remove any unconfirmed files on unmount
    // this could be due to the back button or navigating away
    return (): void => {
      reset();
    };
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (removalStarted) {
      intervalId = setInterval(() => {
        if (rejections.length > 0) {
          setRejections(initial(rejections));
        } else {
          clearInterval(intervalId);
          setRemovalStarted(false);
        }
      }, ALERT_REMOVAL_DURATION);
    }

    return (): void => {
      clearInterval(intervalId);
    };
  }, [rejections, removalStarted]);

  const previousStep = (): void => {
    navigate('/loan-documents');
  };

  const onSubmit = (): void => {
    confirm();
    navigate('/loan-documents/current-mileage');
  };

  const getFileName = (fileName: string): string => {
    const { name, extension } = splitFilename(fileName);

    return name.length > MAXIMUM_FILE_NAME_LENGTH
      ? `${name.slice(0, TRUNCATED_FILE_NAME_PREFIX_LENGTH)}...${name.slice(
          -TRUNCATED_FILE_NAME_SUFFIX_LENGTH,
        )}.${extension}`
      : fileName;
  };

  const getErrorMessage = (error: FileError): string => {
    switch (error.code) {
      case ErrorCode.FileInvalidType:
        return t('dropzone.rejectedFileType');
      case ErrorCode.FileTooLarge:
        return t('dropzone.rejectedFileTooLarge');
      default:
        return error.message;
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      addDocuments(acceptedFiles);

      if (rejectedFiles.length !== 0) {
        const rejectionList = reduce(
          rejectedFiles,
          (list: [string, string][], rejectedFile: FileRejection) => {
            forEach(rejectedFile.errors, (error: FileError) => {
              const fileName = getFileName(rejectedFile.file.name);
              const message = getErrorMessage(error);

              list.push([uniqueId('rejection-'), `${fileName}: ${message}`]);
            });

            return list;
          },
          [],
        );

        setRejections(rejectionList);
      }
    },
    [],
  );

  const clearRejections = (): void => {
    setRemovalStarted(true);
  };

  return (
    <div className="loan-document-upload-all-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          {rejections.length !== 0 && (
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              autoHideDuration={AUTO_HIDE_DURATION}
              open
              onClose={clearRejections}
            >
              <Stack flexDirection="column" gap={1}>
                {rejections.map(([key, message]) => (
                  <Fade key={key} timeout={ALERT_REMOVAL_DURATION} in>
                    <Alert
                      data-testid={
                        locator.pages.loanDocumentUploadAll.alert.error
                      }
                      icon={false}
                      key={key}
                      variant="filled"
                    >
                      {message}
                    </Alert>
                  </Fade>
                ))}
              </Stack>
            </Snackbar>
          )}

          <header data-testid={locator.pages.loanDocumentUploadAll.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.loanDocumentUploadAll.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.loanDocumentUploadAll.header.drawerButton
              }
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Loan Documents completed percentage"
              data-testid={locator.pages.loanDocumentUploadAll.progress.root}
              value={percent}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.loanDocumentUploadAll.form.root}
              noValidate
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.loanDocumentUploadAll.form.h1}>
                  {t('LoanDocumentUploadAllPage.title')}
                </h1>
                <h2 data-testid={locator.pages.loanDocumentUploadAll.form.h2}>
                  {t('LoanDocumentUploadAllPage.subtitle')}
                  <p>{t('LoanDocumentUploadAllPage.subtitleToo')}</p>
                </h2>

                <div
                  className="loan-document-example"
                  data-testid={
                    locator.pages.loanDocumentUploadAll.form.exampleRoot
                  }
                >
                  <span>
                    {t('LoanDocumentUploadAllPage.exampleDocumentsTitle')}
                  </span>
                  <ul>
                    <li>
                      <a
                        data-testid={
                          locator.pages.loanDocumentUploadAll.form
                            .exampleVSCLink
                        }
                        href={config.loanDocuments.vsc.exampleDocumentUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {t('LoanDocumentUploadAllPage.exampleVSCDocument')}
                      </a>
                    </li>
                    <li>
                      <a
                        data-testid={
                          locator.pages.loanDocumentUploadAll.form
                            .exampleGAPLink
                        }
                        href={config.loanDocuments.gap.exampleDocumentUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {t('LoanDocumentUploadAllPage.exampleGAPDocument')}
                      </a>
                    </li>
                    <li>
                      <a
                        data-testid={
                          locator.pages.loanDocumentUploadAll.form
                            .exampleRICLink
                        }
                        href={config.loanDocuments.ric.exampleDocumentUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {t('LoanDocumentUploadAllPage.exampleRICDocument')}
                      </a>
                    </li>
                  </ul>
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <Dropzone isDuplicate={isDuplicateFile} onDrop={onDrop} />

                  <LoanDocumentList providedDocuments={providedDocuments} />

                  {uploadedDocuments.length > 0 && (
                    <LoanDocumentUploadedList
                      uploadedDocuments={uploadedDocuments}
                    />
                  )}
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.loanDocumentUploadAll.form.submitButton
                    }
                    disabled={!hasProvidedDocuments}
                    loadingPosition="start"
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('LoanDocumentUploadAllPage.submit')}
                  </LoadingButton>
                </div>
              </div>
              <footer
                data-testid={locator.pages.loanDocumentUploadAll.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'LoanDocumentUploadAllPage';
